import {
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { ApiError, apiDelete, apiPatch } from "@/shared/api";
import { useCurrentBoardSlug } from "@/shared/hooks/useCurrentBoardSlug";
import { createLimit } from "@/shared/lib/concurrencyLimit";

export type DeleteTarget =
  | { kind: "thread"; threadId: string }
  | { kind: "post"; threadId: string; postNumber: string };

export interface DeleteBulkParams {
  password: string;
  targets: DeleteTarget[];
  onItemDone?: (target: DeleteTarget, result: "ok" | ApiError) => void;
}

export interface DeleteBulkResult {
  succeeded: DeleteTarget[];
  failed: { target: DeleteTarget; error: ApiError }[];
}

// 新API: owner 削除は thread と post で endpoint が分離 (spec §9.5)
//  - thread: PATCH /threads/{slug}/{threadId}/shutdown
//  - post:   DELETE /posts/{slug}/{threadId}/{postNumber}
// 一括 endpoint は無いため p-limit(5) + Promise.allSettled で並列化、
// 全失敗でも resolve し UI 側で failed.length を見て分岐する。
export function useDeleteMutation(): UseMutationResult<
  DeleteBulkResult,
  unknown,
  DeleteBulkParams
> {
  const queryClient = useQueryClient();
  const slug = useCurrentBoardSlug();

  return useMutation({
    mutationFn: async ({
      password,
      targets,
      onItemDone,
    }: DeleteBulkParams): Promise<DeleteBulkResult> => {
      const limit = createLimit(5);
      const tasks = targets.map((target) =>
        limit(async () => {
          try {
            if (target.kind === "thread") {
              await apiPatch<void>(
                `/threads/${slug}/${target.threadId}/shutdown`,
                { password },
              );
            } else {
              await apiDelete<void>(
                `/posts/${slug}/${target.threadId}/${target.postNumber}`,
                { password },
              );
            }
            onItemDone?.(target, "ok");
            return { target, ok: true as const };
          } catch (err) {
            const apiErr =
              err instanceof ApiError ? err : new ApiError(String(err));
            onItemDone?.(target, apiErr);
            return { target, ok: false as const, error: apiErr };
          }
        }),
      );

      const results = await Promise.allSettled(tasks);
      const succeeded: DeleteTarget[] = [];
      const failed: { target: DeleteTarget; error: ApiError }[] = [];
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          if (r.value.ok) {
            succeeded.push(r.value.target);
          } else {
            failed.push({ target: r.value.target, error: r.value.error });
          }
        } else {
          // limit() 自体や予期せぬ throw を拾う (target は順序で復元)
          const apiErr =
            r.reason instanceof ApiError
              ? r.reason
              : new ApiError(String(r.reason));
          console.error("useDeleteMutation task rejected unexpectedly", {
            target: targets[i],
            reason: r.reason,
          });
          failed.push({ target: targets[i], error: apiErr });
        }
      });
      return { succeeded, failed };
    },
    onSuccess: ({ succeeded }: DeleteBulkResult) => {
      // thread 削除分: list と個別 thread / reactions を invalidate
      const deletedThreadIds = new Set(
        succeeded
          .filter(
            (t): t is Extract<DeleteTarget, { kind: "thread" }> =>
              t.kind === "thread",
          )
          .map((t) => t.threadId),
      );
      if (deletedThreadIds.size > 0) {
        queryClient.invalidateQueries({ queryKey: ["threads", slug] });
        for (const threadId of deletedThreadIds) {
          queryClient.invalidateQueries({
            queryKey: ["thread", slug, threadId],
          });
          queryClient.invalidateQueries({
            queryKey: ["reactions", slug, threadId],
          });
        }
      }
      // post 削除分: thread 単位でキャッシュを invalidate (重複排除)
      const postThreadIds = new Set(
        succeeded
          .filter(
            (t): t is Extract<DeleteTarget, { kind: "post" }> =>
              t.kind === "post",
          )
          .map((t) => t.threadId),
      );
      for (const threadId of postThreadIds) {
        queryClient.invalidateQueries({
          queryKey: ["thread", slug, threadId],
        });
        queryClient.invalidateQueries({
          queryKey: ["reactions", slug, threadId],
        });
      }
    },
  });
}
